import React, { useCallback, useEffect, useState } from "react";
import { Button, ButtonGroup, Spinner, Table } from "reactstrap";

import PageHero, { PageHeroRefreshButton } from "components/PageHero";
import FilterBand from "components/FilterBand";
import ShellCard from "components/ShellCard";
import SectionCard from "components/SectionCard";
import SortHeader from "components/SortHeader";
import TablePagination from "components/TablePagination";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { useToast } from "hooks/toast";
import { listEmbarcadores } from "services/onboardingApi";
import { formatDocument } from "utils/formatDoc";
import { formatDateTime } from "utils/formatDate";
import { toggleSort } from "utils/tableHelpers";

// Filtro de status = os 3 recortes do backend (PO1-2897). "pendente" é o
// default e reproduz a lista antiga (embarcadores Status=0).
const STATUS_OPTIONS = [
  { value: "pendente", label: "Em andamento" },
  { value: "concluido", label: "Concluídos" },
  { value: "todos", label: "Todos" },
];
const PAGE_SIZES = [10, 20, 50, 100];
const DEFAULT_SORT = { field: "creationTime", dir: "desc" };

const COLUNAS = [
  { key: "embarcador", label: "Embarcador", sortField: "fantasia" },
  { key: "cnpj", label: "CNPJ" },
  { key: "filiais", label: "Filiais", align: "center" },
  { key: "usuarios", label: "Usuários", align: "center" },
  { key: "status", label: "Status", sortField: "status" },
  { key: "atualizado", label: "Atualizado em", sortField: "lastModificationTime" },
  { key: "criado", label: "Criado em", sortField: "creationTime" },
  { key: "acoes", label: "" },
];

const stateBoxStyle = {
  display: "flex",
  alignItems: "center",
  gap: "0.6rem",
  padding: "1rem 0.25rem",
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.9rem",
};

// Badge na linguagem do checklist (onb-badge): Status=1 = Concluído,
// Status=0 = Em andamento. Não usa components/Status porque ele pinta
// "Inativo" em vermelho, o que rotularia todo onboarding em andamento como erro.
function StatusBadge({ status }) {
  const concluido = Number(status) === 1;
  return (
    <span className={`onb-badge ${concluido ? "done" : "pend"}`}>
      {concluido ? "Concluído" : "Em andamento"}
    </span>
  );
}

// Lista de onboardings (PO1-2897): filtro de status, busca por nome/CNPJ,
// ordenação e paginação server-side (GET /onboarding/embarcadores). Padrão
// visual de Cadastros → Embarcadores (PO1-2728): PageHero + FilterBand +
// Table com SortHeader + TablePagination. Quem abre um item é o pai (index.js),
// que cuida do checklist, do localStorage e do ?id= na URL.
export default function OnboardingLista({ onAbrir, onNovo }) {
  const { addToast } = useToast();
  const [status, setStatus] = useState("pendente");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [data, setData] = useState({ items: [], count: 0 });
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const resultado = await listEmbarcadores({
        status,
        q: q || undefined,
        page,
        pageSize,
        sort: sort.field,
        dir: sort.dir,
      });
      setData({
        items: resultado.items || [],
        count: Number(resultado.count) || 0,
      });
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.error) || err.message;
      setErro(msg);
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao listar onboardings",
        description: msg,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setLoading(false);
    }
  }, [status, q, page, pageSize, sort, addToast]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const trocarStatus = (valor) => {
    if (valor === status) return;
    setStatus(valor);
    setPage(1);
  };

  const handleOrdenar = (field) => {
    setSort((atual) => toggleSort(atual, field));
    setPage(1);
  };

  const vazioTexto =
    status === "pendente"
      ? "Nenhum onboarding em andamento. Comece criando o embarcador a partir do CNPJ."
      : "Nenhum embarcador encontrado para este filtro.";

  const filtrosBotoes = (
    <ButtonGroup size="sm" aria-label="Filtro de status">
      {STATUS_OPTIONS.map((opt) => (
        <Button
          key={opt.value}
          color="info"
          outline={opt.value !== status}
          onClick={() => trocarStatus(opt.value)}
          aria-pressed={opt.value === status}
        >
          {opt.label}
        </Button>
      ))}
    </ButtonGroup>
  );

  return (
    <>
      <PageHero
        eyebrow="Implantações"
        title="Onboarding de Embarcador"
        description="Acompanhe as implantações em andamento, reabra um onboarding concluído e localize qualquer embarcador por nome ou CNPJ."
        actions={
          <>
            <PageHeroRefreshButton onClick={() => carregar()} loading={loading} />
            <Button color="primary" className="btn-sm" onClick={onNovo}>
              + Novo embarcador
            </Button>
          </>
        }
      />

      <FilterBand
        mode="server"
        searchLabel="Buscar onboarding"
        searchHint="Nome fantasia, razão social ou CNPJ (com ou sem máscara)"
        searchPlaceholder="Digite nome ou CNPJ"
        searchValue={qInput}
        onSearchChange={setQInput}
        onSearchSubmit={(valor) => {
          setQ(String(valor || "").trim());
          setPage(1);
        }}
        showFiltersButton={false}
        showActions={false}
        extra={filtrosBotoes}
      />

      <ShellCard>
        <SectionCard
          title="Onboardings"
          description="Clique em uma linha para abrir o checklist. Concluídos abrem em consulta; kit ao sponsor e importação de planilha continuam disponíveis."
          action={
            <span style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem", whiteSpace: "nowrap" }}>
              {data.count} resultado(s)
            </span>
          }
        >
          {loading ? (
            <div style={stateBoxStyle}>
              <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
              <span>Carregando onboardings…</span>
            </div>
          ) : erro ? (
            <div style={stateBoxStyle} role="alert">
              Não foi possível carregar a lista: {erro}
            </div>
          ) : data.items.length === 0 ? (
            <div style={stateBoxStyle}>{vazioTexto}</div>
          ) : (
            <>
              <Table size="sm" responsive hover className="hub-thead-accent">
                <thead className="text-primary">
                  <tr>
                    {COLUNAS.map((col) => (
                      <th
                        key={col.key}
                        className={col.align === "center" ? "text-center" : undefined}
                      >
                        {col.sortField ? (
                          <SortHeader
                            label={col.label}
                            field={col.sortField}
                            sort={sort}
                            onSort={handleOrdenar}
                            align={col.align}
                          />
                        ) : (
                          col.label
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => onAbrir(row.id)}
                      style={{ cursor: "pointer" }}
                      className="onb-pendente-row"
                    >
                      <td>
                        <div
                          style={{
                            color: AUTOPILOT_COLORS.title,
                            fontWeight: 600,
                            marginBottom: "0.15rem",
                          }}
                        >
                          {row.fantasia || row.razaoSocial || "-"}
                        </div>
                        <div
                          style={{
                            color: AUTOPILOT_COLORS.muted,
                            fontSize: "0.78rem",
                            lineHeight: 1.35,
                          }}
                        >
                          {row.fantasia ? row.razaoSocial : "Sem nome fantasia"}
                        </div>
                      </td>
                      <td style={{ color: AUTOPILOT_COLORS.text, whiteSpace: "nowrap" }}>
                        {formatDocument(row.cnpjCpf)}
                      </td>
                      <td className="text-center">{row.filiais}</td>
                      <td className="text-center">{row.usuarios}</td>
                      <td>
                        <StatusBadge status={row.status} />
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {formatDateTime(row.lastModificationTime || row.creationTime)}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {formatDateTime(row.creationTime)}
                      </td>
                      <td className="text-right">
                        <Button
                          size="sm"
                          color="info"
                          outline
                          style={{ whiteSpace: "nowrap" }}
                          onClick={(event) => {
                            event.stopPropagation();
                            onAbrir(row.id);
                          }}
                        >
                          Abrir
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <TablePagination
                page={page}
                pageSize={pageSize}
                total={data.count}
                pageSizes={PAGE_SIZES}
                itemLabel="onboarding(s)"
                onPageChange={setPage}
                onPageSizeChange={(novo) => {
                  setPageSize(novo);
                  setPage(1);
                }}
              />
            </>
          )}
        </SectionCard>
      </ShellCard>
    </>
  );
}
